import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Row } from "antd";
import styled from "styled-components";
import { BranchesOutlined, DownloadOutlined } from "@ant-design/icons";
import { getDateChangeTime, exportExcel } from "../../../../../helper/helper";
import Title from "../../../../components/Title";
import FilterTime from "../../../../components/FilterTime";
import { TYPE_GET_DATE } from "../../../../../helper/const";
import { DualAxes } from "@ant-design/plots";

export default function Overview({ handleGetData, tabActive }) {
  const [startDate, setStartDate] = useState(
    getDateChangeTime(TYPE_GET_DATE.oneMonthAgo)
  );
  const [endDate, setEndDate] = useState(
    getDateChangeTime(TYPE_GET_DATE.now)
  );

  const { result = {} } =
    useSelector((state) => state.reducerNatcom?.data) || {};

  const { data_overview = [], vm_natcom = [] } = result;

  useEffect(() => {
    if (tabActive !== "1") return;
    handleGetData(startDate, endDate);
  }, [startDate, endDate, tabActive, handleGetData]);


  const revenueMap = useMemo(() => {
    const map = {};

    if (!vm_natcom || typeof vm_natcom !== "object") return map;

    Object.keys(vm_natcom).forEach((date) => {
      const arr = vm_natcom[date];

      if (Array.isArray(arr) && arr.length > 0) {
        const last = arr[arr.length - 1];
        map[date] = Number(last?.total_day_0 || 0);
      } else {
        map[date] = 0;
      }
    });

    return map;
  }, [vm_natcom]);

  console.log('revenueMap: ', revenueMap);
  // ===== BUILD CHART DATA FROM data_overview =====
  const { columnData, lineData } = useMemo(() => {
    if (!Array.isArray(data_overview)) {
      return { columnData: [], lineData: [] };
    }

    const columnResult = [];
    const lineResult = [];

    data_overview.forEach((item) => {
      const {
        time,
        total = 0,
        check_sub_error = 0,
        not_enough_money = 0,
        cancel = 0,
        call_invalid = 0,
      } = item;

      const revenue_vm = revenueMap[time] || 0;

      const system_error = total - (check_sub_error + not_enough_money + cancel + call_invalid) - revenue_vm;      

      columnResult.push({
        date: time,
        total,
        type: "Total",
      });

      const calcPercent = (value) =>
        total > 0 ? Number(((value / total) * 100).toFixed(2)) : 0;

      lineResult.push(
        {
          date: time,
          value: check_sub_error,
          percent: calcPercent(check_sub_error),
          type: "Check Sub Error",
        },
        {
          date: time,
          value: not_enough_money,
          percent: calcPercent(not_enough_money),
          type: "Not Enough Money",
        },
        {
          date: time,
          value: cancel,
          percent: calcPercent(cancel),
          type: "Cancel",
        },
        {
          date: time,
          value: call_invalid,
          percent: calcPercent(call_invalid),
          type: "Call Invalid",
        },
        {
          date: time,
          value: system_error > 0 ? system_error : 0,
          percent: calcPercent(system_error),
          type: "System Error",
        }        
      );
    });

    columnResult.sort((a, b) => new Date(a.date) - new Date(b.date));
    lineResult.sort((a, b) => new Date(a.date) - new Date(b.date));

    return { columnData: columnResult, lineData: lineResult };
  }, [data_overview]);

  // ===== EXPORT EXCEL =====
  const handleExportExcel = () => {
    if (!Array.isArray(data_overview) || data_overview.length === 0) {
      console.log("No data available to export");
      return;
    }

    const exportData = {};

    data_overview.forEach((item) => {
      exportData[item.time] = [
        {
          date: item.time,
          total: item.total,
          check_sub_error: item.check_sub_error,
          not_enough_money: item.not_enough_money,
          cancel: item.cancel,
          call_invalid: item.call_invalid,
        },
      ];
    });

    exportExcel(exportData, "miss_call_errors");
  };

  return (
    <>
      <div
        className="position-relative d-flex justify-right mb-2 gap-2"
        style={{ justifyContent: "right" }}
      >
        <FilterTime
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />

        <Button
          onClick={handleExportExcel}
          className="d-flex align-items-center"
        >
          <DownloadOutlined />
          Export Excel
        </Button>
      </div>

      <Container className="panel panel-sortable">
        <Title titleName="Overview">
          <BranchesOutlined />
        </Title>

        <Col span={24}>
          <div className="item-chart">
            <h4 className="pt-3 pe-3 pb-">Overview</h4>

            {columnData.length > 0 ? (
              <DualAxes
                data={[columnData, lineData]}
                xField="date"
                yField={["total", "value"]}
                geometryOptions={[
                  {
                    geometry: "column",
                    color: "#5B8FF9",
                  },
                  {
                    geometry: "line",
                    seriesField: "type",
                    color: ["#2FC3F2", "#30BF78", "#FAAD14", "#8543E0", "#ee5d64"],
                    lineStyle: { lineWidth: 2 },
                    point: { size: 3, shape: "circle" },
                  },
                ]}
                tooltip={{
                  shared: true,
                  showCrosshairs: true,
                  formatter: (item) => {
                    if (!item.type) {
                      return {
                        name: "Total",
                        value: item.total?.toLocaleString("vi-VN"),
                      };
                    }

                    const totalItem = columnData.find(
                      (c) => c.date === item.date
                    );

                    const percent = totalItem
                      ? ((item.value / totalItem.total) * 100).toFixed(2)
                      : 0;

                    return {
                      name: item.type,
                      value: `${item.value?.toLocaleString(
                        "vi-VN"
                      )} (${percent}%)`,
                    };
                  },
                }}
                meta={{
                  total: {
                    alias: "Total",
                    formatter: (v) => v?.toLocaleString(),
                  },
                  value: {
                    alias: "Error Count",
                    formatter: (v) => v?.toLocaleString(),
                  },
                }}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "50px" }}>
                No data available
              </div>
            )}
          </div>
        </Col>
      </Container>
    </>
  );
}

const Container = styled(Row)``;
